import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getPolicyImportCatalogue, importPolicyRows } from "@/lib/api";
import { errMsg, errList, SEL, Stat } from "./policyShared";

export const IMPORT_GROUPS = [
  { key: "hard_stops", title: "Hard stops", keyCol: "id" },
  { key: "flags", title: "Flags", keyCol: "id" },
  { key: "industry_list", title: "Industry list", keyCol: "trade_name" },
  { key: "lender_table", title: "Lender table", keyCol: "id" },
];

const CSV_HINT = {
  hard_stops: 'id,text,scope,clause,test\nHS_X,Company status not active,pre_screen,Part 2,"{""field"":""inputs.company.status"",""operator"":""not_in"",""value"":[""active""]}"',
  flags: 'id,text,evidence,clause,trigger,written_reason_required\nF_X,Unsatisfied CCJ,CCJ register,Part 2,"{""field"":""derived.ccj_unsatisfied_count"",""operator"":"">"",""value"":0}",true',
  industry_list: "trade_name,sic_codes,band,non_homeowner_excluded,customer_type,notes\nTaxi,49320;49390,low,false,consumer_facing,",
  lender_table: "id,name,aliases,payee_patterns,classification,treatment,charge_registration_name,collection_route,in_our_market\ncubefunder,Cubefunder,Cube Funder,TALLAGHT FINANCIAL,short_term_daily_weekly,deduct,Tallaght Financial Ltd,direct_debit,true",
};

function Pills({ items, tone, label, testId }) {
  if (!items?.length) return null;
  return (
    <div className="text-xs" data-testid={testId}>
      <span className="text-slate-500 mr-1">{label}:</span>
      {items.slice(0, 40).map((k, i) => <Badge key={i} variant="outline" className={`mr-1 mb-1 text-[10px] font-mono ${tone}`}>{typeof k === "string" ? k : `${k.key} (${k.fields.join(", ")})`}</Badge>)}
      {items.length > 40 && <span className="text-slate-400">+{items.length - 40} more</span>}
    </div>
  );
}

// §F load path: preview then apply catalogue / pasted rows for hard stops, flags, industry list or lender table into a draft.
export function ImportDialog({ open, draftId, initialGroup = "hard_stops", onClose, onApplied }) {
  const [group, setGroup] = useState(initialGroup);
  const [source, setSource] = useState("catalogue");
  const [mode, setMode] = useState("merge");
  const [paste, setPaste] = useState("");
  const [reason, setReason] = useState("");
  const [catalogue, setCatalogue] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [errors, setErrors] = useState([]);

  useEffect(() => { if (open) { setGroup(initialGroup); setSource("catalogue"); setMode("merge"); setPaste(""); setReason(""); setPreview(null); setErrors([]); } }, [open, initialGroup]);
  useEffect(() => {
    if (!open) return;
    setCatalogue(null); setPreview(null);
    getPolicyImportCatalogue(group).then(setCatalogue).catch((e) => toast.error(errMsg(e)));
  }, [open, group]);

  const body = () => {
    const b = { source, mode, reason };
    if (source === "paste") {
      const t = paste.trim();
      if (t.startsWith("[")) { try { b.rows = JSON.parse(t); } catch (e) { throw new Error(`Pasted JSON is invalid: ${e.message}`); } }
      else b.csv = t;
    }
    return b;
  };
  const run = async (apply) => {
    setBusy(apply ? "apply" : "preview"); setErrors([]);
    try {
      const res = await importPolicyRows(draftId, group, { ...body(), apply });
      if (apply) { toast.success(`Imported into ${IMPORT_GROUPS.find((g) => g.key === group)?.title}: ${res.summary}`); onApplied?.(res); onClose(); }
      else setPreview(res);
    } catch (e) {
      setErrors(errList(e).length ? errList(e) : [{ error: errMsg(e) }]);
    } finally { setBusy(""); }
  };
  const canApply = preview && !preview.validation_errors?.length && (preview.added?.length || preview.updated?.length || preview.removed?.length) && reason.trim();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="policy-import-dialog">
        <DialogHeader>
          <DialogTitle>Load rows into this draft</DialogTitle>
          <DialogDescription>Bulk-load a decision table from the portal's built-in catalogue or from pasted CSV / JSON. Preview first; applying records one reasoned change the approver sees row by row.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-xs text-slate-500 flex flex-col gap-1">Table
              <select className={SEL} value={group} onChange={(e) => setGroup(e.target.value)} data-testid="policy-import-group">{IMPORT_GROUPS.map((g) => <option key={g.key} value={g.key}>{g.title}</option>)}</select>
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1">Source
              <select className={SEL} value={source} onChange={(e) => { setSource(e.target.value); setPreview(null); }} data-testid="policy-import-source">
                <option value="catalogue">Portal catalogue</option><option value="paste">Paste CSV / JSON</option>
              </select>
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1 min-w-0 max-w-full">Mode
              <select className={`${SEL} max-w-full truncate`} value={mode} onChange={(e) => { setMode(e.target.value); setPreview(null); }} data-testid="policy-import-mode">
                <option value="merge">Merge — add new rows, update matching, keep the rest</option><option value="replace">Replace — the import becomes the whole list</option>
              </select>
            </label>
          </div>
          {source === "catalogue" && catalogue && (
            <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-2 space-y-1" data-testid="policy-import-catalogue-info">
              <div className="font-semibold text-slate-700">{catalogue.rows.length} rows available{catalogue.invalid_rows?.length ? ` · ${catalogue.invalid_rows.length} invalid skipped` : ""}</div>
              {catalogue.audit && (
                <div className={`font-medium ${catalogue.audit.never_run ? "text-rose-700" : catalogue.audit.stale ? "text-amber-800" : "text-emerald-700"}`} data-testid="policy-import-audit-status">• {catalogue.audit.message}</div>
              )}
              {(catalogue.notes || []).map((n, i) => <div key={i} className="text-slate-600">• {n}</div>)}
            </div>
          )}
          {source === "paste" && (
            <div>
              <label className="text-xs text-slate-500">CSV (header row = column names; lists separated by ; · conditions as JSON) or a JSON array of rows</label>
              <Textarea value={paste} onChange={(e) => { setPaste(e.target.value); setPreview(null); }} rows={7} className="font-mono text-xs max-w-full" placeholder={CSV_HINT[group]} data-testid="policy-import-paste" />
              <div className="text-[11px] text-slate-400 mt-1">Columns: {(catalogue?.columns || []).join(", ")}</div>
            </div>
          )}
          <div>
            <label className="text-xs text-slate-500">Reason (required to apply)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. Load the procedure's hard stops as the starting set for v2.0" data-testid="policy-import-reason" />
          </div>
          {preview && (
            <div className="space-y-2" data-testid="policy-import-preview">
              <div className="flex flex-wrap gap-2">
                <Stat label="Incoming" value={preview.incoming} testId="policy-import-incoming" />
                <Stat label="Added" value={preview.added.length} tone="text-emerald-700" testId="policy-import-added" />
                <Stat label="Updated" value={preview.updated.length} tone="text-blue-700" testId="policy-import-updated" />
                <Stat label="Unchanged" value={preview.unchanged.length} testId="policy-import-unchanged" />
                <Stat label="Removed" value={preview.removed.length} tone={preview.removed.length ? "text-rose-700" : ""} testId="policy-import-removed" />
                {preview.preserved?.length > 0 && <Stat label="Policy rows kept" value={preview.preserved.length} tone="text-violet-700" testId="policy-import-preserved" />}
                <Stat label="Rows after" value={`${preview.before} → ${preview.after}`} testId="policy-import-after" />
              </div>
              <Pills items={preview.added} tone="bg-emerald-50 text-emerald-700 border-emerald-300" label="Added" testId="policy-import-added-list" />
              <Pills items={preview.updated} tone="bg-blue-50 text-blue-700 border-blue-300" label="Updated" testId="policy-import-updated-list" />
              <Pills items={preview.removed} tone="bg-rose-50 text-rose-700 border-rose-300" label="Removed" testId="policy-import-removed-list" />
              <Pills items={preview.preserved || []} tone="bg-violet-50 text-violet-700 border-violet-300" label="Policy rows kept (source policy / notes 'Part N')" testId="policy-import-preserved-list" />
              {preview.invalid_rows?.length > 0 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="policy-import-invalid">
                  <div className="font-semibold">{preview.invalid_rows.length} row(s) skipped</div>
                  {preview.invalid_rows.slice(0, 8).map((r, i) => <div key={i}>row {r.row}{r.key ? ` (${r.key})` : ""}: {(r.errors || [r.error]).join("; ")}</div>)}
                </div>
              )}
              {preview.validation_errors?.length > 0 && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2" data-testid="policy-import-validation">
                  <div className="font-semibold">Import would fail validation</div>
                  {preview.validation_errors.slice(0, 8).map((er, i) => <div key={i}><code>{er.path}</code> {er.error}</div>)}
                </div>
              )}
            </div>
          )}
          {errors.length > 0 && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2 space-y-0.5" data-testid="policy-import-errors">
              {errors.map((er, i) => <div key={i}>{er.path ? <code>{er.path}</code> : null} {er.error || er.message || JSON.stringify(er)}</div>)}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="policy-import-cancel">Cancel</Button>
          <Button variant="outline" onClick={() => run(false)} disabled={!!busy || (source === "paste" && !paste.trim())} data-testid="policy-import-preview-btn">{busy === "preview" && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Preview</Button>
          <Button onClick={() => run(true)} disabled={!!busy || !canApply} className="bg-emerald-600 hover:bg-emerald-700" title={!preview ? "Preview first" : !reason.trim() ? "A reason is required" : ""} data-testid="policy-import-apply">{busy === "apply" && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Apply to draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
